import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { CiCalculationResult } from '../../../_services/ci.service';
import { ORIENTATION_OPTIONS, PREMIUM_OPTIONS, ROAD_OPTIONS } from '../config/ci-options.config';
import { LandValueForm, PropertyCategory } from '../models/ci-form.model';
import { UiCalculatedBoxComponent } from '../ui/ui-calculated-box.component';
import { UiManualInputComponent } from '../ui/ui-manual-input.component';
import { UiNoteBarComponent } from '../ui/ui-note-bar.component';
import { formatCompactCurrency, formatSignedPct } from '../../../core/utils/format.util';

/**
 * Plot / Land adjustments, shared by plots and independent houses.
 * Inputs only — every figure shown comes from the backend land assessment.
 */
@Component({
  selector: 'ci-step-land-adjustments',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, UiCalculatedBoxComponent, UiManualInputComponent, UiNoteBarComponent],
  templateUrl: './step-land-adjustments.component.html',
})
export class StepLandAdjustmentsComponent {
  readonly form = input.required<LandValueForm>();
  readonly category = input.required<PropertyCategory>();
  /** Backend result for exactly the current inputs, or null while recalculating. */
  readonly result = input<CiCalculationResult | null>(null);
  readonly calculating = input(false);
  readonly calculationError = input<string | null>(null);

  readonly orientationOptions = ORIENTATION_OPTIONS;
  readonly roadOptions = ROAD_OPTIONS;
  readonly premiumOptions = PREMIUM_OPTIONS;

  compact = formatCompactCurrency;

  get orientation() { return this.form().controls.orientation; }
  get roadAdjustment() { return this.form().controls.roadAdjustment; }
  get premiumAdjustment() { return this.form().controls.premiumAdjustment; }
  get supplyOverride() { return this.form().controls.supplyOverride; }
  get supplyOverrideValue() { return this.form().controls.supplyOverrideValue; }
  get supplyOverrideReason() { return this.form().controls.supplyOverrideReason; }

  /** Reason is mandatory with an override; flagged once the analyst has touched it. */
  reasonMissing(): boolean {
    const c = this.supplyOverrideReason;
    return this.supplyOverride.value && !c.value?.trim() && (c.touched || c.dirty);
  }

  valueMissing(): boolean {
    const c = this.supplyOverrideValue;
    return this.supplyOverride.value && c.value == null && (c.touched || c.dirty);
  }

  /** Override total as typed, always in crore: "₹4.00 Cr (₹4,00,00,000)". */
  overrideInCrore(): string | null {
    const n = Number(this.supplyOverrideValue.value);
    if (this.supplyOverrideValue.value == null || !Number.isFinite(n) || n <= 0) return null;
    return `₹${(n / 1e7).toFixed(2)} Cr (₹${Math.round(n).toLocaleString('en-IN')})`;
  }

  /** Clears the override so the calculated land value is used again. */
  clearOverride(): void {
    this.form().patchValue({ supplyOverride: false, supplyOverrideValue: null, supplyOverrideReason: '' });
    this.form().markAsDirty();
  }

  isPlot(): boolean {
    return this.category() === 'plot-land';
  }

  /** Land figures from the backend; null until a fresh result exists. */
  land() {
    return this.result()?.landAssessment ?? null;
  }

  /**
   * Step 10 with the figures filled in. A plot has no building value, so
   * Final CI Value = Final Land Value.
   */
  finalCiFormula(): string {
    const a = this.land();
    if (!a) return 'Step 10 · Final Land Value (a plot has no building value)';
    const value = this.compact(a.assessedValue);
    if (a.supplyOverrideApplied) {
      return `Final CI Value = Supply Override total = ${value}`;
    }
    const t = a.totalLandAdjustment;
    const adj = `${t < 0 ? '−' : '+'} ${+(Math.abs(t) * 100).toFixed(2)}%`;
    return 'Final CI Value = Plot Area × Circled Land Rate × (1 + Total Land Adjustment) = '
      + `${a.plotArea.toLocaleString('en-IN')} sq ft × ₹${a.landRatePsf.toLocaleString('en-IN')} × (1 ${adj}) = ${value}`;
  }

  /** Step 11 as a plain ratio, e.g. 0.0471. */
  ratio(value: number | null | undefined): string {
    return value == null ? '—' : value.toFixed(4);
  }

  pct(fraction: number | null | undefined, digits = 1): string {
    return fraction == null ? '—' : formatSignedPct(fraction * 100, digits);
  }
}
